import { SectionCard } from "@/components/common/SectionCard";
import {
  ghostBtnClass,
  mfgPrimaryBtnClass,
} from "@/components/manufacturing/manufacturingSurfaceClasses";
import type { NamedVariantSummary } from "@/lib/manufacturing/namedVariantsClient";
import { ManufacturingEntityBulkBar } from "./ManufacturingEntityBulkBar";
import type { useEntityBulkSelection } from "./useEntityBulkSelection";

type Bulk = ReturnType<typeof useEntityBulkSelection<NamedVariantSummary>>;

export function ManufacturingNamedVariantsListCard({
  enabled,
  saveBusy,
  listBusy,
  variants,
  selectedId,
  creating,
  bulk,
  exportBusy,
  localError,
  onBeginCreate,
  onBulkImport,
  onReload,
  onPickVariant,
  onExportSelected,
  onExportAll,
}: {
  enabled: boolean;
  saveBusy: boolean;
  listBusy: boolean;
  variants: readonly NamedVariantSummary[];
  selectedId: string | null;
  creating: boolean;
  bulk: Bulk;
  exportBusy: boolean;
  localError: string | null;
  onBeginCreate: () => void;
  onBulkImport: () => void;
  onReload: () => void;
  onPickVariant: (id: string) => void;
  onExportSelected: () => void;
  onExportAll: () => void;
}) {
  return (
    <SectionCard
      title="Named variants"
      description="One upgrade identity applied to many recipes. Pending applications show ! until confirmed."
    >
      <div className="mb-3 flex flex-wrap gap-2">
        <button
          type="button"
          className={mfgPrimaryBtnClass}
          onClick={onBeginCreate}
          disabled={!enabled || saveBusy}
        >
          + New named variant
        </button>
        <button
          type="button"
          className={ghostBtnClass}
          onClick={onBulkImport}
          disabled={!enabled}
        >
          Bulk import
        </button>
        <button type="button" className={ghostBtnClass} onClick={onReload} disabled={listBusy}>
          {listBusy ? "Refreshing…" : "Refresh"}
        </button>
      </div>
      <ManufacturingEntityBulkBar
        entityLabel="named variant"
        selectedCount={bulk.selectedCount}
        filteredCount={variants.length}
        allFilteredSelected={bulk.allFilteredSelected}
        busy={exportBusy}
        onSelectAllFiltered={bulk.selectAllFiltered}
        onClear={bulk.clearSelection}
        onExportCsv={onExportSelected}
        onExportAllFiltered={onExportAll}
        hint="Header-only export (code, name, description, status). Round-trips Bulk import."
      />
      {localError ? (
        <div className="mb-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900 dark:border-red-800 dark:bg-red-950/35 dark:text-red-100">
          {localError}
        </div>
      ) : null}
      <ul className="max-h-[28rem] space-y-1 overflow-y-auto">
        {variants.map((row) => {
          const active = row.id === selectedId && !creating;
          return (
            <li key={row.id} className="flex items-start gap-2">
              <input
                type="checkbox"
                className="mt-2.5"
                checked={bulk.selectedIds.has(row.id)}
                onChange={() => bulk.toggleSelected(row.id)}
                aria-label={`Select ${row.code}`}
              />
              <button
                type="button"
                className={
                  "flex min-w-0 flex-1 items-center justify-between rounded-lg px-3 py-2 text-left text-sm " +
                  (active
                    ? "bg-white shadow-sm dark:bg-zinc-900"
                    : "hover:bg-white/70 dark:hover:bg-zinc-900/60")
                }
                onClick={() => onPickVariant(row.id)}
              >
                <span>
                  <span className="font-semibold text-gray-900 dark:text-zinc-100">
                    {row.code}
                  </span>{" "}
                  <span className="text-gray-600 dark:text-zinc-300">{row.name}</span>
                  {row.status === "archived" ? (
                    <span className="ml-2 text-xs text-gray-400">archived</span>
                  ) : null}
                </span>
                {row.pending_application_count > 0 ? (
                  <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-bold text-amber-900 dark:bg-amber-950 dark:text-amber-100">
                    ! {row.pending_application_count}
                  </span>
                ) : (
                  <span className="text-xs text-gray-400">{row.application_count} apps</span>
                )}
              </button>
            </li>
          );
        })}
        {variants.length === 0 && !listBusy ? (
          <li className="px-2 py-6 text-center text-sm text-gray-500 dark:text-zinc-400">
            No named variants yet.
          </li>
        ) : null}
      </ul>
    </SectionCard>
  );
}
